import React, { useCallback, useState } from "react";
import {
  IonContent,
  IonBadge,
  IonButton,
  IonButtons,
  IonIcon,
  IonAlert,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonFooter,
  useIonToast,
} from "@ionic/react";
import {
  calendarOutline,
  timeOutline,
  mapOutline,
  closeOutline,
  chevronForwardOutline,
  personOutline,
  businessOutline,
  documentTextOutline,
  cashOutline,
  informationCircleOutline,
  shieldCheckmarkOutline,
  callOutline,
} from "ionicons/icons";
import { Capacitor } from "@capacitor/core";
import moment from "moment";
import { MisAsignacionesResponse } from "src/services/misAsignaciones";
import OperariosRemplazos from "src/services/operarioRemplazo";

// ---- Tipos ----
export type TipoCaso =
  | "reemplazo"
  | "asignacion"
  | "orden_servicio"
  | "limpieza_profunda"
  | "refuerzo"
  | "dia_libre"
  | "vacaciones"
  | "permiso";

export interface Caso {
  tipo: TipoCaso;
  item: any;
}

export const emptyData: MisAsignacionesResponse = {
  asignaciones: [],
  ordenes_servicio: [],
  reemplazos: [],
  limpiezas_profundas: [],
  novedades_cc: [],
};

// Mapea el campo `tipo` de las novedades del calendario CC al TipoCaso de la UI
const TIPO_NOVEDAD_CC: Record<string, TipoCaso> = {
  REFUERZO: "refuerzo",
  DIA_LIBRE: "dia_libre",
  VACACIONES: "vacaciones",
  PERMISO: "permiso",
};

const TIPOS_NOVEDAD_CC: TipoCaso[] = [
  "refuerzo",
  "dia_libre",
  "vacaciones",
  "permiso",
];

// ---- Constantes ----
// getDay(): 0 = domingo ... 6 = sabado
export const DIA_KEYS = ["dom", "lun", "mar", "mie", "jue", "vie", "sab"];
const DIAS = ["lun", "mar", "mie", "jue", "vie", "sab", "dom"];

const DIAS_TABLA = [
  { key: "lun", label: "Lun" },
  { key: "mar", label: "Mar" },
  { key: "mie", label: "Mie" },
  { key: "jue", label: "Jue" },
  { key: "vie", label: "Vie" },
  { key: "sab", label: "Sab" },
  { key: "dom", label: "Dom" },
];

export const CONFIG_TIPO: Record<
  TipoCaso,
  { etiqueta: string; color: string }
> = {
  reemplazo: { etiqueta: "Reemplazo", color: "primary" },
  asignacion: { etiqueta: "Asignacion", color: "tertiary" },
  orden_servicio: { etiqueta: "Orden de servicio", color: "warning" },
  limpieza_profunda: { etiqueta: "Limpieza profunda", color: "success" },
  refuerzo: { etiqueta: "Refuerzo", color: "secondary" },
  dia_libre: { etiqueta: "Dia libre", color: "medium" },
  vacaciones: { etiqueta: "Vacaciones", color: "dark" },
  permiso: { etiqueta: "Permiso", color: "warning" },
};

export const GARNET = "#910434";

const cardSeccionStyle: React.CSSProperties = {
  background: "#ffffff",
  borderRadius: 16,
  padding: 16,
  marginBottom: 14,
  boxShadow: "0 2px 10px rgba(0,0,0,.06)",
};

// ---- Helpers ----
const formatTime = (value?: string) => {
  if (!value) return "";
  const txt = String(value);
  return txt.length >= 5 ? txt.slice(0, 5) : txt;
};

const formatDate = (value?: string) =>
  value && moment(value).isValid() ? moment(value).format("DD/MM/YYYY") : "-";

const tieneHorarioAlgunDia = (item: any) =>
  DIAS.some((k) => item[`${k}_ent`] || item[`${k}_sal`]);

// Normaliza "lat lng" (separado por espacio) o "lat, lng" a "lat,lng"
const normalizarCoords = (coords: string) =>
  coords.trim().replace(/\s+/g, ",").replace(/,+/g, ",");

export const abrirEnMapa = (coordenadas?: string, direccion?: string) => {
  const coords = normalizarCoords(coordenadas || "");
  const dir = (direccion || "").trim();
  const esNativo = Capacitor.isNativePlatform();

  const query = coords || (dir ? encodeURIComponent(dir) : "");
  if (!query) return;

  if (esNativo) {
    // En el dispositivo abre la app de mapas nativa (Google Maps / Waze / etc.)
    const geo = coords ? `geo:${coords}?q=${coords}` : `geo:0,0?q=${query}`;
    window.open(geo, "_system");
    return;
  }
  window.open(
    `https://www.google.com/maps/search/?api=1&query=${query}`,
    "_blank"
  );
};

// Horario del caso para un dia de la semana dado (keyDia = lun/mar/...)
export const renderHorarioCaso = (caso: Caso, keyDia: string) => {
  const { tipo, item } = caso;
  if (tipo === "orden_servicio") {
    const ini = formatTime(item.hora_inicio);
    const fin = formatTime(item.hora_fin);
    return ini || fin ? `${ini} - ${fin}` : "";
  }
  const ent = formatTime(item[`${keyDia}_ent`] || item.hora_inicio);
  const sal = formatTime(item[`${keyDia}_sal`] || item.hora_fin);
  return ent || sal ? `${ent} - ${sal}` : "";
};

// Filtra los casos que aplican a una fecha dada (hoy o futuro)
export const filtrarCasosPorFecha = (
  data: MisAsignacionesResponse,
  fecha: moment.Moment
): Caso[] => {
  const dia = fecha.clone().startOf("day");
  const keyDia = DIA_KEYS[dia.day()];

  const aplicaDia = (item: any) =>
    Boolean(item[`${keyDia}_ent`] || item[`${keyDia}_sal`]);

  const enRango = (ini?: string, fin?: string) => {
    if (ini) {
      const i = moment(ini).startOf("day");
      if (i.isValid() && dia.isBefore(i, "day")) return false;
    }
    if (fin) {
      const f = moment(fin).startOf("day");
      if (f.isValid() && dia.isAfter(f, "day")) return false;
    }
    return true;
  };

  // Recurrentes: dentro del rango y con horario configurado ese dia
  // (si no tiene ningun horario por dia, se muestra igual si esta vigente)
  const aplicaRecurrente = (item: any, ini?: string, fin?: string) => {
    if (!enRango(ini, fin)) return false;
    if (!tieneHorarioAlgunDia(item)) return true;
    return aplicaDia(item);
  };

  const casos: Caso[] = [];

  (data.reemplazos || []).forEach((item: any) => {
    if (
      aplicaRecurrente(
        item,
        item.fecha_inicio || item.fecha,
        item.fecha_fin || item.fechaFin
      )
    ) {
      casos.push({ tipo: "reemplazo", item });
    }
  });

  (data.asignaciones || []).forEach((item: any) => {
    if (aplicaRecurrente(item, item.fecha_inicio, item.fecha_fin)) {
      casos.push({ tipo: "asignacion", item });
    }
  });

  (data.ordenes_servicio || []).forEach((item: any) => {
    const f = moment(item.fecha).startOf("day");
    if (f.isValid() && f.isSame(dia, "day")) {
      casos.push({ tipo: "orden_servicio", item });
    }
  });

  (data.limpiezas_profundas || []).forEach((item: any) => {
    const iniSame =
      moment(item.fecha_inicio).isValid() &&
      moment(item.fecha_inicio).isSame(dia, "day");
    if (iniSame || aplicaRecurrente(item, item.fecha_inicio, item.fecha_fin)) {
      casos.push({ tipo: "limpieza_profunda", item });
    }
  });

  // Novedades del calendario CC: registros de un solo dia (campo fecha)
  (data.novedades_cc || []).forEach((item: any) => {
    const tipoNovedad = TIPO_NOVEDAD_CC[String(item.tipo || "").toUpperCase()];
    if (!tipoNovedad) return;
    const f = moment(item.fecha).startOf("day");
    if (f.isValid() && f.isSame(dia, "day")) {
      casos.push({ tipo: tipoNovedad, item });
    }
  });

  return casos;
};

export const renderBadgeEstado = (tipo: TipoCaso, estadoConf: string) => {
  if (tipo !== "reemplazo") return null;
  if (estadoConf === "CONFIRMADO")
    return <IonBadge color="success">Confirmado</IonBadge>;
  if (estadoConf === "RECHAZADO")
    return <IonBadge color="danger">Rechazado</IonBadge>;
  if (estadoConf === "PENDIENTE")
    return <IonBadge color="warning">Pendiente</IonBadge>;
  return null;
};

// ---- Sub-componentes ----
const InfoRow: React.FC<{ icon: string; label: string; value?: any }> = ({
  icon,
  label,
  value,
}) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div
      style={{ display: "flex", gap: 12, padding: "10px 0", alignItems: "flex-start" }}
    >
      <IonIcon
        icon={icon}
        style={{ color: GARNET, fontSize: 20, marginTop: 2, flexShrink: 0 }}
      />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, color: "#8a8a8a" }}>{label}</div>
        <div style={{ fontSize: 15, color: "#222", fontWeight: 500 }}>{value}</div>
      </div>
    </div>
  );
};

const TelefonoLink: React.FC<{ telefono?: string }> = ({ telefono }) => {
  const tel = String(telefono || "").trim();
  if (!tel) return null;
  return (
    <a
      href={`tel:${tel}`}
      style={{ color: GARNET, fontWeight: 600, textDecoration: "none", fontSize: 14 }}
    >
      <IonIcon icon={callOutline} style={{ fontSize: 14, verticalAlign: -2, marginRight: 4 }} />
      {tel}
    </a>
  );
};

const HorarioSemanal: React.FC<{ item: any; keyDia: string; esHoy: boolean }> = ({
  item,
  keyDia,
  esHoy,
}) => {
  const diasConHorario = DIAS_TABLA.filter(
    (d) => item[`${d.key}_ent`] || item[`${d.key}_sal`]
  );
  if (diasConHorario.length === 0) return null;
  return (
    <div style={cardSeccionStyle}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: "#555",
          marginBottom: 8,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        Horario semanal
      </div>
      {diasConHorario.map((d) => {
        const marcado = d.key === keyDia;
        const ent = formatTime(item[`${d.key}_ent`]);
        const sal = formatTime(item[`${d.key}_sal`]);
        return (
          <div
            key={`dia-${d.key}`}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 12px",
              borderRadius: 10,
              marginBottom: 6,
              background: marcado ? GARNET : "#f6f6f6",
            }}
          >
            <span style={{ fontWeight: 700, fontSize: 14, color: marcado ? "#fff" : "#333" }}>
              {d.label}
              {marcado && esHoy ? " (Hoy)" : ""}
            </span>
            <span style={{ fontSize: 14, color: marcado ? "#fff" : "#333" }}>
              {ent} - {sal}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// Tarjeta compacta de un caso
export const CasoCard: React.FC<{
  caso: Caso;
  keyDia: string;
  onClick: () => void;
}> = ({ caso, keyDia, onClick }) => {
  const { tipo, item } = caso;
  const cfg = CONFIG_TIPO[tipo];
  const horario = renderHorarioCaso(caso, keyDia);
  const estadoConf = String(item.estado_confirmacion || "").toUpperCase();
  return (
    <div
      onClick={onClick}
      style={{
        background: "#ffffff",
        border: "1px solid #eee",
        borderRadius: 12,
        padding: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,.06)",
        cursor: "pointer",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: 4,
        }}
      >
        <IonBadge color={cfg.color}>{cfg.etiqueta}</IonBadge>
        {renderBadgeEstado(tipo, estadoConf)}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h3 style={{ margin: "2px 0", fontWeight: 600 }}>
            {item.punto_servicio || cfg.etiqueta}
          </h3>
          {horario ? (
            <p style={{ margin: "2px 0", display: "flex", alignItems: "center", gap: 6 }}>
              <IonIcon icon={timeOutline} />
              {horario}
            </p>
          ) : null}
        </div>
        <IonIcon
          icon={chevronForwardOutline}
          style={{ color: GARNET, flexShrink: 0 }}
        />
      </div>
    </div>
  );
};

// Modal de detalle del caso, con Confirmar/Rechazar y Abrir Mapa
export const CasoDetalleModal: React.FC<{
  caso: Caso | null;
  keyDia: string;
  esHoy: boolean;
  diaLabel: string;
  onClose: () => void;
  onEstadoCambiado: (confirmacionId: number, estado: string) => void;
}> = ({ caso, keyDia, esHoy, diaLabel, onClose, onEstadoCambiado }) => {
  const [accionId, setAccionId] = useState<number | null>(null);
  const [itemARechazar, setItemARechazar] = useState<any | null>(null);
  const [present] = useIonToast();

  const handleConfirmar = useCallback(
    async (confirmacionId: number) => {
      if (!confirmacionId || accionId) return;
      setAccionId(confirmacionId);
      try {
        const resp: any = await OperariosRemplazos.confirmarRemplazo(confirmacionId);
        if (resp?.data?.ok) {
          onEstadoCambiado(confirmacionId, "CONFIRMADO");
          present({ message: "Asistencia confirmada.", duration: 3000, color: "success" });
        } else {
          present({
            message: resp?.data?.error || "No se pudo confirmar la asistencia.",
            duration: 4000,
            color: "warning",
          });
        }
      } catch (err: any) {
        const msg = err?.response?.data?.error || "No se pudo confirmar la asistencia.";
        present({ message: msg, duration: 4000, color: "danger" });
      } finally {
        setAccionId(null);
      }
    },
    [accionId, present, onEstadoCambiado]
  );

  const handleRechazar = useCallback(
    async (confirmacionId: number, motivo: string) => {
      const motivoTrim = String(motivo || "").trim();
      if (!confirmacionId || accionId) return;
      if (!motivoTrim) {
        present({ message: "El motivo del rechazo es obligatorio.", duration: 3000, color: "warning" });
        return;
      }
      setAccionId(confirmacionId);
      try {
        const resp: any = await OperariosRemplazos.rechazarRemplazo(confirmacionId, motivoTrim);
        if (resp?.data?.ok) {
          onEstadoCambiado(confirmacionId, "RECHAZADO");
          present({ message: "Reemplazo rechazado.", duration: 3000, color: "success" });
        } else {
          present({
            message: resp?.data?.error || "No se pudo rechazar el reemplazo.",
            duration: 4000,
            color: "warning",
          });
        }
      } catch (err: any) {
        const msg = err?.response?.data?.error || "No se pudo rechazar el reemplazo.";
        present({ message: msg, duration: 4000, color: "danger" });
      } finally {
        setAccionId(null);
      }
    },
    [accionId, present, onEstadoCambiado]
  );

  return (
    <>
      <IonModal isOpen={!!caso} onDidDismiss={onClose}>
        {caso
          ? (() => {
              const { tipo, item } = caso;
              const cfg = CONFIG_TIPO[tipo];
              const estadoConf = String(item.estado_confirmacion || "").toUpperCase();
              const direccion = (item.direccion_ps || "").trim();
              const coords = (item.coordenadas || "").trim();
              const puedeMapa = Boolean(direccion || coords);
              const esReemplazoPendiente =
                tipo === "reemplazo" && estadoConf === "PENDIENTE" && item.confirmacion_id;
              const cargando = accionId === item.confirmacion_id;
              const horarioDia = renderHorarioCaso(caso, keyDia);
              const hayAcciones = puedeMapa || esReemplazoPendiente;
              return (
                <>
                  <IonHeader>
                    <IonToolbar color="primary">
                      <IonTitle>{cfg.etiqueta}</IonTitle>
                      <IonButtons slot="end">
                        <IonButton onClick={onClose}>
                          <IonIcon slot="icon-only" icon={closeOutline} />
                        </IonButton>
                      </IonButtons>
                    </IonToolbar>
                  </IonHeader>
                  <IonContent style={{ "--background": "#f4f5f8" } as any}>
                    <div style={{ padding: 16 }}>
                      {/* Encabezado */}
                      <div style={cardSeccionStyle}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          <IonBadge color={cfg.color}>{cfg.etiqueta}</IonBadge>
                          {renderBadgeEstado(tipo, estadoConf)}
                        </div>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                          <IonIcon
                            icon={businessOutline}
                            style={{ color: GARNET, fontSize: 24, marginTop: 2, flexShrink: 0 }}
                          />
                          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#1a1a1a" }}>
                            {item.punto_servicio || cfg.etiqueta}
                          </h2>
                        </div>
                        {horarioDia ? (
                          <div
                            style={{
                              marginTop: 12,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              background: "#fdecec",
                              color: GARNET,
                              padding: "10px 12px",
                              borderRadius: 10,
                              fontWeight: 700,
                              fontSize: 15,
                            }}
                          >
                            <IonIcon icon={timeOutline} style={{ fontSize: 20 }} />
                            {diaLabel}: {horarioDia}
                          </div>
                        ) : null}
                      </div>

                      {/* Datos */}
                      <div style={cardSeccionStyle}>
                        {tipo === "orden_servicio" || TIPOS_NOVEDAD_CC.includes(tipo) ? (
                          <InfoRow icon={calendarOutline} label="Fecha" value={formatDate(item.fecha)} />
                        ) : (
                          <InfoRow
                            icon={calendarOutline}
                            label="Vigencia"
                            value={`${formatDate(item.fecha_inicio || item.fecha)}${
                              item.fecha_fin || item.fechaFin
                                ? ` - ${formatDate(item.fecha_fin || item.fechaFin)}`
                                : ""
                            }`}
                          />
                        )}

                        {TIPOS_NOVEDAD_CC.includes(tipo) ? (
                          <InfoRow
                            icon={documentTextOutline}
                            label="Motivo"
                            value={item.motivo || null}
                          />
                        ) : null}

                        {tipo === "reemplazo" ? (
                          <InfoRow
                            icon={personOutline}
                            label="Reemplazas a"
                            value={
                              item.nombre_reemplazado || item.legajo_reemplazado ? (
                                <>
                                  <div>{item.nombre_reemplazado || item.legajo_reemplazado}</div>
                                  <TelefonoLink telefono={item.telefono_reemplazado} />
                                </>
                              ) : null
                            }
                          />
                        ) : null}

                        {tipo === "orden_servicio" ? (
                          <>
                            <InfoRow icon={timeOutline} label="Tipo de hora" value={item.tipo_hora || null} />
                            <InfoRow icon={cashOutline} label="Pago doble" value={item.pago_doble ? "Si" : "No"} />
                            <InfoRow icon={documentTextOutline} label="Observacion" value={item.observacion || null} />
                          </>
                        ) : null}

                        {tipo === "asignacion" || tipo === "limpieza_profunda" ? (
                          <InfoRow icon={timeOutline} label="Horas" value={item.total_horas || null} />
                        ) : null}

                        {tipo === "asignacion" ? (
                          <InfoRow icon={informationCircleOutline} label="Estado" value={item.estado || null} />
                        ) : null}

                        <InfoRow
                          icon={shieldCheckmarkOutline}
                          label="Fiscal a cargo"
                          value={
                            item.fiscal_nombre ? (
                              <>
                                <div>{item.fiscal_nombre}</div>
                                <TelefonoLink telefono={item.fiscal_telefono} />
                              </>
                            ) : null
                          }
                        />
                      </div>

                      <HorarioSemanal item={item} keyDia={keyDia} esHoy={esHoy} />
                    </div>
                  </IonContent>

                  {hayAcciones ? (
                    <IonFooter>
                      <IonToolbar>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "8px 12px" }}>
                          {puedeMapa ? (
                            <IonButton expand="block" onClick={() => abrirEnMapa(coords, direccion)}>
                              <IonIcon slot="start" icon={mapOutline} />
                              Abrir Mapa
                            </IonButton>
                          ) : null}
                          {esReemplazoPendiente ? (
                            <IonButton
                              expand="block"
                              color="success"
                              disabled={cargando}
                              onClick={() => handleConfirmar(item.confirmacion_id)}
                            >
                              {cargando ? "Confirmando..." : "Confirmar Asistencia"}
                            </IonButton>
                          ) : null}
                          {esReemplazoPendiente ? (
                            <IonButton
                              expand="block"
                              color="danger"
                              fill="outline"
                              disabled={cargando}
                              onClick={() => setItemARechazar(item)}
                            >
                              Rechazar
                            </IonButton>
                          ) : null}
                        </div>
                      </IonToolbar>
                    </IonFooter>
                  ) : null}
                </>
              );
            })()
          : null}
      </IonModal>

      <IonAlert
        isOpen={!!itemARechazar}
        onDidDismiss={() => setItemARechazar(null)}
        header="Rechazar reemplazo"
        message="Indica el motivo del rechazo (obligatorio)."
        inputs={[{ name: "motivo", type: "textarea", placeholder: "Motivo del rechazo" }]}
        buttons={[
          { text: "Cancelar", role: "cancel" },
          {
            text: "Rechazar",
            handler: (valores) => {
              const motivo = String(valores?.motivo || "").trim();
              if (!motivo) {
                present({ message: "El motivo del rechazo es obligatorio.", duration: 3000, color: "warning" });
                return false;
              }
              handleRechazar(itemARechazar?.confirmacion_id, motivo);
              return true;
            },
          },
        ]}
      />
    </>
  );
};
